import {
  IntervalType,
  MetricsCountDto,
  MetricsDepositsDto,
  MetricsIBCommissionsDto,
  MetricsLotsDto,
  MetricsNetDepositsDto,
  MetricsNVDto,
} from '@/api/Metrics/Types/MetricsDto'
import { ComparisonChartDataItem } from '@/components/Chart/ComparisonChart'
import { format } from 'date-fns'
import { enUS } from 'date-fns/locale'

import {
  ACTIVE_CLIENTS_METRICS_CATEGORY,
  ACTIVE_TA_METRICS_CATEGORY,
  DEPOSITS_METRICS_CATEGORY,
  FTD_METRICS_CATEGORY,
  IB_COMMISSIONS_METRICS_CATEGORY,
  LOTS_METRICS_CATEGORY,
  NET_DEPOSITS_METRICS_CATEGORY,
  NEW_CLIENTS_METRICS_CATEGORY,
  NV_METRICS_CATEGORY,
  OPENED_TA_METRICS_CATEGORY,
} from '../config'

type TMetrics =
  | MetricsCountDto
  | MetricsDepositsDto
  | MetricsIBCommissionsDto
  | MetricsLotsDto
  | MetricsNetDepositsDto
  | MetricsNVDto

export type TData =
  | MetricsCountDto[]
  | MetricsDepositsDto[]
  | MetricsIBCommissionsDto[]
  | MetricsLotsDto[]
  | MetricsNetDepositsDto[]
  | MetricsNVDto[]

export type TMetricsCategory =
  | typeof NV_METRICS_CATEGORY
  | typeof LOTS_METRICS_CATEGORY
  | typeof FTD_METRICS_CATEGORY
  | typeof DEPOSITS_METRICS_CATEGORY
  | typeof NET_DEPOSITS_METRICS_CATEGORY
  | typeof NEW_CLIENTS_METRICS_CATEGORY
  | typeof ACTIVE_CLIENTS_METRICS_CATEGORY
  | typeof OPENED_TA_METRICS_CATEGORY
  | typeof ACTIVE_TA_METRICS_CATEGORY
  | typeof IB_COMMISSIONS_METRICS_CATEGORY

export const processChartData = (data: TData | undefined, category: TMetricsCategory) => {
  if (!data) {
    return []
  }

  return data.map((metrics, index) => {
    const date = metrics.endDate

    const shortFormat = metrics.intervalType.id === IntervalType.Daily ? 'd MMM' : 'd MMM'
    const longFormat = metrics.intervalType.id === IntervalType.Daily ? 'd MMMM' : 'MMMM yyyy'

    const name = format(new Date(date), shortFormat, { locale: enUS })
    const tooltipLabel = format(new Date(date), longFormat, { locale: enUS })

    return {
      name: name.toUpperCase(),
      tooltipLabel: tooltipLabel.toUpperCase(),
      value: getChartValueByMetricsType(metrics, category),
      date,
      index,
    }
  })
}

const getChartValueByMetricsType = (metrics: TMetrics, category: TMetricsCategory) => {
  switch (category) {
    case NV_METRICS_CATEGORY:
      return 'notionalVolumeUsd' in metrics ? metrics.notionalVolumeUsd : 0
    case LOTS_METRICS_CATEGORY:
      return 'lots' in metrics ? metrics.lots : 0
    case FTD_METRICS_CATEGORY:
      return 'count' in metrics ? metrics.count : 0
    case DEPOSITS_METRICS_CATEGORY:
      return 'totalDeposits' in metrics ? metrics.totalDeposits : 0
    case NET_DEPOSITS_METRICS_CATEGORY:
      return 'totalNetDeposits' in metrics ? metrics.totalNetDeposits : 0
    case NEW_CLIENTS_METRICS_CATEGORY:
      return 'count' in metrics ? metrics.count : 0
    case ACTIVE_CLIENTS_METRICS_CATEGORY:
      return 'count' in metrics ? metrics.count : 0
    case OPENED_TA_METRICS_CATEGORY:
      return 'count' in metrics ? metrics.count : 0
    case ACTIVE_TA_METRICS_CATEGORY:
      return 'count' in metrics ? metrics.count : 0
    case IB_COMMISSIONS_METRICS_CATEGORY:
      return 'commission' in metrics ? metrics.commission : 0
    default:
      return 0
  }
}

export const getTotalValueByMetricsType = (
  data: TData | null | undefined,
  category: TMetricsCategory
): number => {
  if (!data) {
    return 0
  }

  return data.reduce((sum, metrics) => {
    return sum + getChartValueByMetricsType(metrics, category)
  }, 0)
}

export const processComparisonChartData = (
  primaryData: TData | null,
  secondaryData: TData | null,
  primaryCategory?: TMetricsCategory,
  secondaryCategory?: TMetricsCategory
) => {
  if (!primaryData) {
    return []
  }

  const primary = processChartData(primaryData, primaryCategory as TMetricsCategory)

  const secondary =
    secondaryData && secondaryCategory ? processChartData(secondaryData, secondaryCategory) : []

  const map = new Map<string, ComparisonChartDataItem>()

  primary.forEach((item) => {
    map.set(item.date, {
      name: item.name,
      tooltipLabel: item.tooltipLabel,
      value1: item.value,
      value2: 0,
      date: item.date,
      index: item.index,
    })
  })

  secondary.forEach((item) => {
    const existing = map.get(item.date)

    if (existing) {
      existing.value2 = item.value
    }
  })

  return Array.from(map.values())
}
